import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../providers/GlobalProvider';
import { Link, useLocation } from 'react-router-dom';
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from 'react-circular-progressbar';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DataObjectIcon from '@mui/icons-material/DataObjectOutlined';
import PriceCheckIcon from '@mui/icons-material/PriceCheckOutlined';
import { BuildOutlined } from '@mui/icons-material';

const SHOW_BENEFICIAL_OWNERSHIP_LINK = false;

const NewSidebar: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const location = useLocation();
  const [daysLeft, setDaysLeft] = useState({ isLoading: true, data: 0 });
  const [customer, setCustomer] = useState<any>({ isLoading: true, data: {} });
  const [isTrialing, setIsTrialing] = useState({
    isLoading: true,
    data: false,
  });

  const [expanded, setExpanded] = useState<string | false>(false);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const createPanelRef = useRef<HTMLDivElement | null>(null);
  const createTriggerRef = useRef<HTMLButtonElement | null>(null);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const isQuickcodeOnly = Boolean(cur_user.quickcodeOnly);
  const isRestricted = cur_user?.role === 'domain-restricted';

  const getDaysLeft = (trialEnd: number) => {
    const dateNow = Date.now() / 1000;
    const delta = Math.floor(trialEnd - dateNow);
    const days = Math.round(delta / 86400);
    setDaysLeft({ isLoading: false, data: days });
  };

  const getMatchedCus = () => {
    if (cur_user.stripe_cus_info) {
      cur_user.stripe_cus_info.map((cus: any) => {
        if (cus.name === cur_user?.selected_team?.name) {
          setCustomer({ isLoading: false, data: cus });
        }
      });
    }
  };

  const validateTrialing = (cus: any) => {
    cus?.data?.subscriptions?.data?.map((sub: any) => {
      if (sub.status === 'trialing') {
        setIsTrialing({ isLoading: false, data: true });
      } else {
        setIsTrialing({ isLoading: false, data: false });
      }
    });
  };

  useEffect(() => {
    getMatchedCus();
  }, [cur_user.selected_team]);

  useEffect(() => {
    getDaysLeft(customer?.data?.subscriptions?.data[0]?.trial_end);
    validateTrialing(customer);
  }, [customer]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isCreatePanelOpen) return;

      const target = event.target as Node;
      if (
        createPanelRef.current?.contains(target) ||
        createTriggerRef.current?.contains(target)
      ) {
        return;
      }

      setIsCreatePanelOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsCreatePanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isCreatePanelOpen]);

  useEffect(() => {
    setIsCreatePanelOpen(false);
  }, [location.pathname, location.state]);

  if (isQuickcodeOnly) return null;

  const isRouteActive = (
    path: string,
    exact = false,
    stateKey?: string,
    stateVal?: string,
  ) => {
    const pathMatches = exact
      ? location.pathname === path
      : location.pathname.includes(path);
    if (stateKey && stateVal) {
      const currentStateVal = (location.state as any)?.[stateKey];
      return pathMatches && currentStateVal === stateVal;
    }
    return pathMatches;
  };

  const isCustomsEntryAnalyzerActive = isRouteActive('/customs-entry-analyzer');
  const isWorkflowsActive =
    isRouteActive('/import-page') ||
    isRouteActive('/export-page') ||
    isRouteActive('/export-design') ||
    isRouteActive('/beneficial-ownership-workflow') ||
    isCustomsEntryAnalyzerActive;
  const isBiteDbActive =
    isRouteActive('/bite-db/entities') ||
    isRouteActive('/bite-db/products') ||
    isRouteActive('/export-hub') ||
    isRouteActive('/import-hub');
  const isAnalyzeActive =
    isRouteActive('/ace-analyzers') ||
    isRouteActive('/tariff-analyzers') ||
    isRouteActive('/aes-analyzers');
  const isToolsActive =
    isRouteActive('/entity-screening') ||
    isRouteActive('/bite-classify') ||
    isRouteActive('/tariff-checker') ||
    isRouteActive('/import-screening') ||
    isRouteActive('/export-screening') ||
    isRouteActive('/bite-eccn');
  const isResourcesActive =
    isRouteActive('/bite-ai') ||
    isRouteActive('/industry-updates') ||
    isRouteActive('/data/sets') ||
    isRouteActive('/faq');

  const createMenuSections = [
    {
      title: 'Workflows',
      links: [
        { label: 'Run Import Workflow', to: '/import-page' },
        { label: 'Run Export Workflow', to: '/export-page' },
        { label: 'Run Design Assistant', to: '/export-design' },
      ],
    },
    {
      title: 'BITE DB',
      links: [
        { label: 'Create Import DB', to: '/import-hub/databases' },
        { label: 'Create Export DB', to: '/export-hub/databases' },
        { label: 'Create Entity DB', to: '/bite-db/entities' },
      ],
    },
    {
      title: 'Analyzers',
      links: [
        { label: 'Run ACE Analysis', to: '/ace-analyzers' },
        { label: 'Run Tariff Analysis', to: '/tariff-analyzers' },
        { label: 'Run AES Analysis', to: '/aes-analyzers' },
      ],
    },
    {
      title: 'Tools',
      links: [
        { label: 'Entity Screen', to: '/entity-screening' },
        { label: 'Classify', to: '/bite-classify' },
        { label: 'Tariff Calculator', to: '/tariff-checker' },
        // Document Parsing Workflow — local/QA only (gated like Reports /
        // Export Hub). Omitted from the Create panel entirely in prod.

        {
          label: 'Import Screen',
          to: {
            pathname: '/import-screening',
            state: { linkTab: 'Entities' },
          },
        },
        {
          label: 'Export Screen',
          to: {
            pathname: '/export-screening',
            state: { linkTab: 'Entities' },
          },
        },
        { label: 'ECCN Check', to: '/bite-eccn' },
      ],
    },
  ];

  const activeLinkSx = {
    backgroundColor: 'rgba(72, 149, 239, 0.15)',
    color: '#4895EF !important',
    position: 'relative',
    borderRadius: '4px',
    '& > a': { color: '#4895EF !important' },
    '& svg': { color: '#4895EF !important' },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      height: '60%',
      width: '4px',
      backgroundColor: '#4895EF',
      borderTopRightRadius: '4px',
      borderBottomRightRadius: '4px',
    },
  };

  const getSummarySx = (isActiveWhenClosed: boolean) => ({
    padding: '8px 10px',
    margin: 0,
    minHeight: '32px !important',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'var(--bg-hover)',
      borderRadius: '4px',
      color: 'var(--text-secondary)',
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
    },
    '& .accordion-arrow': {
      transition: 'transform 0.2s',
    },
    '&.Mui-expanded .accordion-arrow': {
      transform: 'translateY(-50%) rotate(180deg)',
    },
    ...(isActiveWhenClosed ? activeLinkSx : {}),
  });

  if (isRestricted) {
    return (
      <div className='sideNav'>
        <div className='SideNav-Body'>
          <Box
            className='Navigation'
            sx={{
              paddingTop: '20px',
              paddingBottom: '25px',
              '& a span': {
                fontWeight: '300 !important',
              },
              '& svg': {
                fontSize: '26px !important',
              },
            }}
          >
            <ul>
              <Box
                component='li'
                sx={isRouteActive('/entity-screening') ? activeLinkSx : {}}
              >
                <Link
                  to='/entity-screening'
                  style={{
                    color: isRouteActive('/entity-screening')
                      ? '#4895EF'
                      : 'inherit',
                  }}
                >
                  <PersonSearchOutlinedIcon />
                  <span>Entity Screen</span>
                </Link>
              </Box>
              <Box
                component='li'
                sx={isRouteActive('/bite-monitor') ? activeLinkSx : {}}
              >
                <Link
                  to='/bite-monitor'
                  style={{
                    color: isRouteActive('/bite-monitor')
                      ? '#4895EF'
                      : 'inherit',
                  }}
                >
                  <BookmarkBorderOutlinedIcon />
                  <span>Saved Work</span>
                </Link>
              </Box>
              <Box
                component='li'
                sx={
                  isRouteActive('/profile', false, 'linkTab', 'Profile')
                    ? activeLinkSx
                    : {}
                }
              >
                <Link
                  to={{ pathname: '/profile', state: { linkTab: 'Profile' } }}
                  style={{
                    color: isRouteActive(
                      '/profile',
                      false,
                      'linkTab',
                      'Profile',
                    )
                      ? '#4895EF'
                      : 'inherit',
                  }}
                >
                  <PersonOutlineOutlinedIcon />
                  <span>Account</span>
                </Link>
              </Box>
              <Box
                component='li'
                sx={isRouteActive('/faq') ? activeLinkSx : {}}
              >
                <Link
                  to='/faq'
                  style={{
                    color: isRouteActive('/faq') ? '#4895EF' : 'inherit',
                  }}
                >
                  <ContactSupportOutlinedIcon />
                  <span>Help Center</span>
                </Link>
              </Box>
            </ul>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`sideNav${isCreatePanelOpen ? ' sideNav--create-open' : ''}`}
    >
      <div className='SideNav-Body'>
        <Box
          className='Navigation'
          sx={{
            paddingBottom: '25px', // Moved bottom padding here so the bottom elements don't hit the trial box
            '& a span, & .sidebar-menu-span, & .sidebar-menu-parent span': {
              fontWeight: '300 !important',
            },
            '& svg:not(.accordion-arrow)': {
              fontSize: '26px !important',
            },
            '& .accordion-arrow': {
              fontSize: '20px !important',
            },
          }}
        >
          <ul>
            <li className='create-nav-item'>
              <button
                ref={createTriggerRef}
                type='button'
                className={`create-nav-trigger${
                  isCreatePanelOpen ? ' is-open' : ''
                }`}
                onClick={() => setIsCreatePanelOpen((prev) => !prev)}
                aria-expanded={isCreatePanelOpen}
                aria-controls='create-nav-panel'
              >
                <AddOutlinedIcon />
                <span>Create</span>
              </button>
            </li>

            <li>
              <Accordion
                expanded={expanded === 'workflows'}
                onChange={handleAccordionChange('workflows')}
                disableGutters
                sx={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    backgroundColor: 'rgba(72, 149, 239, 0.08)',
                    borderRadius: '8px',
                    margin: '0',
                  },
                }}
              >
                <AccordionSummary
                  sx={getSummarySx(
                    isWorkflowsActive && expanded !== 'workflows',
                  )}
                >
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <RouteOutlinedIcon sx={{ color: 'inherit' }} />
                    <ExpandMoreIcon
                      className='accordion-arrow'
                      sx={{
                        position: 'absolute',
                        right: -20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'inherit',
                      }}
                    />
                  </Box>
                  <span className='sidebar-menu-span'>Workflows</span>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box
                    component='ul'
                    sx={{ padding: 0, margin: 0, listStyle: 'none' }}
                  >
                    <Box
                      component='li'
                      sx={isRouteActive('/import-page') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/import-page'
                        style={{
                          color: isRouteActive('/import-page')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <DownloadOutlinedIcon />
                        <span>Import</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/export-page') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/export-page'
                        style={{
                          color: isRouteActive('/export-page')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <PublishOutlinedIcon />
                        <span>Export</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/export-design') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/export-design'
                        style={{
                          color: isRouteActive('/export-design')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <TuneOutlinedIcon />
                        <span>Design Assistant</span>
                      </Link>
                    </Box>
                    {SHOW_BENEFICIAL_OWNERSHIP_LINK && (
                      <Box
                        component='li'
                        sx={
                          isRouteActive('/beneficial-ownership-workflow')
                            ? activeLinkSx
                            : {}
                        }
                      >
                        <Link
                          to='/beneficial-ownership-workflow'
                          style={{
                            color: isRouteActive(
                              '/beneficial-ownership-workflow',
                            )
                              ? '#4895EF'
                              : 'inherit',
                          }}
                        >
                          <ManageSearchOutlinedIcon />
                          <span>Beneficial Ownership</span>
                        </Link>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </li>

            <li>
              <Accordion
                expanded={expanded === 'bitedb'}
                onChange={handleAccordionChange('bitedb')}
                disableGutters
                sx={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    backgroundColor: 'rgba(72, 149, 239, 0.08)',
                    borderRadius: '8px',
                    margin: '0',
                  },
                }}
              >
                <AccordionSummary
                  sx={getSummarySx(isBiteDbActive && expanded !== 'bitedb')}
                >
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <DnsOutlinedIcon sx={{ color: 'inherit' }} />
                    <ExpandMoreIcon
                      className='accordion-arrow'
                      sx={{
                        position: 'absolute',
                        right: -20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'inherit',
                      }}
                    />
                  </Box>
                  <span className='sidebar-menu-span'>BITE DB</span>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box
                    component='ul'
                    sx={{ padding: 0, margin: 0, listStyle: 'none' }}
                  >
                    <Box
                      component='li'
                      sx={
                        isRouteActive('/bite-db/entities') ? activeLinkSx : {}
                      }
                    >
                      <Link
                        to='/bite-db/entities'
                        style={{
                          color: isRouteActive('/bite-db')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <GroupOutlinedIcon />
                        <span>Entities</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/export-hub') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/export-hub'
                        style={{
                          color: isRouteActive('/export-hub')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <PublishOutlinedIcon />
                        <span>Exports</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/import-hub') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/import-hub'
                        style={{
                          color: isRouteActive('/import-hub')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <Inventory2OutlinedIcon />
                        <span>Imports</span>
                      </Link>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </li>
            <li>
              <Accordion
                expanded={expanded === 'analyze'}
                onChange={handleAccordionChange('analyze')}
                disableGutters
                sx={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    backgroundColor: 'rgba(72, 149, 239, 0.08)',
                    borderRadius: '8px',
                    margin: '0',
                  },
                }}
              >
                <AccordionSummary
                  sx={getSummarySx(isAnalyzeActive && expanded !== 'analyze')}
                >
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <TroubleshootOutlinedIcon sx={{ color: 'inherit' }} />
                    <ExpandMoreIcon
                      className='accordion-arrow'
                      sx={{
                        position: 'absolute',
                        right: -20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'inherit',
                      }}
                    />
                  </Box>
                  <span className='sidebar-menu-span'>Analyzers</span>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box
                    component='ul'
                    sx={{ padding: 0, margin: 0, listStyle: 'none' }}
                  >
                    <Box
                      component='li'
                      sx={isRouteActive('/ace-analyzers') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/ace-analyzers'
                        style={{
                          color: isRouteActive('/ace-analyzers')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <AccountTreeOutlinedIcon />
                        <span>ACE Analyzer</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={
                        isRouteActive('/tariff-analyzers') ? activeLinkSx : {}
                      }
                    >
                      <Link
                        to='/tariff-analyzers'
                        style={{
                          color: isRouteActive('/tariff-analyzers')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <QueryStatsOutlinedIcon />
                        <span>Tariff Analyzer</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/aes-analyzers') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/aes-analyzers'
                        style={{
                          color: isRouteActive('/aes-analyzers')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <FactCheckOutlinedIcon />
                        <span>AES Analyzer</span>
                      </Link>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </li>
            <li>
              <Accordion
                expanded={expanded === 'tools'}
                onChange={handleAccordionChange('tools')}
                disableGutters
                sx={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    backgroundColor: 'rgba(72, 149, 239, 0.08)',
                    borderRadius: '8px',
                    margin: '0',
                  },
                }}
              >
                <AccordionSummary
                  sx={getSummarySx(isToolsActive && expanded !== 'tools')}
                >
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <BuildOutlined sx={{ color: 'inherit' }} />
                    <ExpandMoreIcon
                      className='accordion-arrow'
                      sx={{
                        position: 'absolute',
                        right: -20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'inherit',
                      }}
                    />
                  </Box>
                  <span className='sidebar-menu-span'>Tools</span>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box
                    component='ul'
                    sx={{ padding: 0, margin: 0, listStyle: 'none' }}
                  >
                    <Box
                      component='li'
                      sx={
                        isRouteActive('/entity-screening') ? activeLinkSx : {}
                      }
                    >
                      <Link
                        to='/entity-screening'
                        style={{
                          color: isRouteActive('/entity-screening')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <PersonSearchOutlinedIcon />
                        <span>Entity Screen</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/bite-classify') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/bite-classify'
                        style={{
                          color: isRouteActive('/bite-classify')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <CategoryOutlinedIcon />
                        <span>Classify</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/tariff-checker') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/tariff-checker'
                        style={{
                          color: isRouteActive('/tariff-checker')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <PriceCheckIcon />
                        <span>Check Tariffs</span>
                      </Link>
                    </Box>

                    <Box
                      component='li'
                      sx={
                        isRouteActive(
                          '/import-screening',
                          false,
                          'linkTab',
                          'Entities',
                        )
                          ? activeLinkSx
                          : {}
                      }
                    >
                      <Link
                        to={{
                          pathname: '/import-screening',
                          state: { linkTab: 'Entities' },
                        }}
                        style={{
                          color: isRouteActive(
                            '/import-screening',
                            false,
                            'linkTab',
                            'Entities',
                          )
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <TravelExploreOutlinedIcon />
                        <span>Import Screen</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={
                        isRouteActive(
                          '/export-screening',
                          false,
                          'linkTab',
                          'Entities',
                        )
                          ? activeLinkSx
                          : {}
                      }
                    >
                      <Link
                        to={{
                          pathname: '/export-screening',
                          state: { linkTab: 'Entities' },
                        }}
                        style={{
                          color: isRouteActive(
                            '/export-screening',
                            false,
                            'linkTab',
                            'Entities',
                          )
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <ManageSearchOutlinedIcon />
                        <span>Export Screen</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/bite-eccn') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/bite-eccn'
                        style={{
                          color: isRouteActive('/bite-eccn')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <VerifiedOutlinedIcon />
                        <span>ECCN Check</span>
                      </Link>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </li>
            <li>
              <Accordion
                expanded={expanded === 'resources'}
                onChange={handleAccordionChange('resources')}
                disableGutters
                sx={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    backgroundColor: 'rgba(72, 149, 239, 0.08)',
                    borderRadius: '8px',
                    margin: '0',
                  },
                }}
              >
                <AccordionSummary
                  sx={getSummarySx(
                    isResourcesActive && expanded !== 'resources',
                  )}
                >
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <LocalLibraryOutlinedIcon sx={{ color: 'inherit' }} />
                    <ExpandMoreIcon
                      className='accordion-arrow'
                      sx={{
                        position: 'absolute',
                        right: -20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'inherit',
                      }}
                    />
                  </Box>
                  <span className='sidebar-menu-span'>Resources</span>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box
                    component='ul'
                    sx={{ padding: 0, margin: 0, listStyle: 'none' }}
                  >
                    <Box
                      component='li'
                      sx={isRouteActive('/bite-ai') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/bite-ai'
                        style={{
                          color: isRouteActive('/bite-ai')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <ForumOutlinedIcon />
                        <span>Ask</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={
                        isRouteActive('/industry-updates') ? activeLinkSx : {}
                      }
                    >
                      <Link
                        to='/industry-updates'
                        style={{
                          color: isRouteActive('/industry-updates')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <FeedOutlinedIcon />
                        <span>Industry News</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/data/sets') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/data/sets'
                        style={{
                          color: isRouteActive('/data/sets')
                            ? '#4895EF'
                            : 'inherit',
                        }}
                      >
                        <DataObjectIcon />
                        <span>Data Catalog</span>
                      </Link>
                    </Box>
                    <Box
                      component='li'
                      sx={isRouteActive('/faq') ? activeLinkSx : {}}
                    >
                      <Link
                        to='/faq'
                        style={{
                          color: isRouteActive('/faq') ? '#4895EF' : 'inherit',
                        }}
                      >
                        <ContactSupportOutlinedIcon />
                        <span>Help Center</span>
                      </Link>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </li>
          </ul>

          <ul>
            <Box
              component='li'
              sx={isRouteActive('/bite-monitor') ? activeLinkSx : {}}
            >
              <Link
                to='/bite-monitor'
                style={{
                  color: isRouteActive('/bite-monitor') ? '#4895EF' : 'inherit',
                }}
              >
                <BookmarkBorderOutlinedIcon />
                <span>Saved Work</span>
              </Link>
            </Box>
            {/* <Box
              component='li'
              sx={isRouteActive('/bite-viz') ? activeLinkSx : {}}
            >
              <Link
                to='/bite-viz'
                style={{
                  color: isRouteActive('/bite-viz') ? '#4895EF' : 'inherit',
                }}
              >
                <EqualizerOutlinedIcon />
                <span>Visualize</span>
              </Link>
            </Box> */}
          </ul>
          <ul>
            {!cur_user.awsUser && (
              <Box
                component='li'
                sx={
                  isRouteActive('/profile', false, 'linkTab', 'Profile')
                    ? activeLinkSx
                    : {}
                }
              >
                <Link
                  to={{ pathname: '/profile', state: { linkTab: 'Profile' } }}
                  style={{
                    color: isRouteActive(
                      '/profile',
                      false,
                      'linkTab',
                      'Profile',
                    )
                      ? '#4895EF'
                      : 'inherit',
                  }}
                >
                  <PersonOutlineOutlinedIcon />
                  <span>Account</span>
                </Link>
              </Box>
            )}
          </ul>
        </Box>
      </div>
      {isCreatePanelOpen && (
        <div
          id='create-nav-panel'
          ref={createPanelRef}
          className='create-nav-panel'
          role='dialog'
          aria-label='Create menu'
          style={{ width: '600px' }}
        >
          <div className='create-nav-panel-header'>
            <span className='create-nav-panel-title'>Create</span>
            <button
              type='button'
              className='create-nav-panel-close'
              onClick={() => setIsCreatePanelOpen(false)}
              aria-label='Close create menu'
            >
              <CloseOutlinedIcon />
            </button>
          </div>
          <div
            className='create-nav-panel-grid'
            style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
          >
            {createMenuSections.map((section) => (
              <div key={section.title} className='create-nav-panel-section'>
                <div className='create-nav-panel-section-title'>
                  {section.title}
                </div>
                <div className='create-nav-panel-links '>
                  {section.links.map((link) => (
                    <Link
                      key={link.label}
                      className='create-nav-panel-link'
                      to={link.to as any}
                      onClick={() => setIsCreatePanelOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Trial Status Component Output Intentionally Omitted Here for Brevity - It remains unchanged */}{' '}
      {!cur_user.awsUser &&
        !daysLeft.isLoading &&
        !isTrialing.isLoading &&
        isTrialing.data && (
          <div className='free-trial-ovw'>
            {isTrialing.data ? (
              <div className='free-trial-days-left'>
                <div className='days-left-graph'>
                  <CircularProgressbarWithChildren
                    value={daysLeft.data}
                    maxValue={14}
                    styles={buildStyles({
                      strokeLinecap: 'round',
                      pathTransitionDuration: 0.5,
                      pathColor: '#4895EF',
                      trailColor: '#4A4C51',
                    })}
                  >
                    <div className='days-left-inner'>
                      <div className='days-left-number'>{daysLeft.data}</div>
                      <div className='days-left-subtext'>days left</div>
                    </div>
                  </CircularProgressbarWithChildren>
                </div>
                <div className='trial-section-action'>
                  <Link
                    to={{
                      pathname: '/profile',
                      state: {
                        linkTab: 'Plans',
                        team: cur_user.selected_team,
                      },
                    }}
                  >
                    <div className='trial-upgrade-btn'>
                      {isTrialing.data ? (
                        <div className='trial-upgrade-btn-text'>Upgrade</div>
                      ) : (
                        <div className='trial-upgrade-btn-text'>
                          Get Started
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              </div>
            ) : (
              <div className='no-trial-container'>
                <div className='no-trial-avatar'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='36'
                    height='36'
                    viewBox='0 0 36 36'
                    fill='none'
                  >
                    <g clipPath='url(#clip0_6025_39726)'>
                      <path
                        d='M0 18C0 8.05888 8.05888 0 18 0C27.9411 0 36 8.05888 36 18C36 27.9411 27.9411 36 18 36C8.05888 36 0 27.9411 0 18Z'
                        fill='#4895EF'
                        fillOpacity='0.15'
                      />
                      <path
                        d='M0.25 18C0.25 8.19695 8.19695 0.25 18 0.25C27.8031 0.25 35.75 8.19695 35.75 18C35.75 27.8031 27.8031 35.75 18 35.75C8.19695 35.75 0.25 27.8031 0.25 18Z'
                        stroke='#EFF1F6'
                        strokeWidth='0.5'
                      />
                      <g clipPath='url(#clip1_6025_39726)'>
                        <path
                          d='M12.375 25.4375V21.6875M12.375 14.1875V10.4375M10.5 12.3125H14.25M10.5 23.5625H14.25M18.75 11.1875L17.4494 14.5691C17.2379 15.1191 17.1321 15.394 16.9676 15.6253C16.8219 15.8303 16.6428 16.0094 16.4378 16.1551C16.2065 16.3196 15.9316 16.4254 15.3816 16.6369L12 17.9375L15.3816 19.2381C15.9316 19.4496 16.2065 19.5554 16.4378 19.7199C16.6428 19.8656 16.8219 20.0447 16.9676 20.2497C17.1321 20.481 17.2379 20.7559 17.4494 21.3059L18.75 24.6875L20.0506 21.3059C20.2621 20.7559 20.3679 20.481 20.5324 20.2497C20.6781 20.0447 20.8572 19.8656 21.0622 19.7199C21.2935 19.5554 21.5684 19.4496 22.1184 19.2381L25.5 17.9375L22.1184 16.6369C21.5684 16.4254 21.2935 16.3196 21.0622 16.1551C20.8572 16.0094 20.6781 15.8303 20.5324 15.6253C20.3679 15.394 20.2621 15.1191 20.0506 14.5691L18.75 11.1875Z'
                          stroke='#4895EF'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </g>
                    </g>
                    <path
                      d='M0.5 18C0.5 8.33502 8.33502 0.5 18 0.5C27.665 0.5 35.5 8.33502 35.5 18C35.5 27.665 27.665 35.5 18 35.5C8.33502 35.5 0.5 27.665 0.5 18Z'
                      stroke='#4895EF'
                    />
                    <defs>
                      <clipPath id='clip0_6025_39726'>
                        <path
                          d='M0 18C0 8.05888 8.05888 0 18 0C27.9411 0 36 8.05888 36 18C36 27.9411 27.9411 36 18 36C8.05888 36 0 27.9411 0 18Z'
                          fill='white'
                        />
                      </clipPath>
                      <clipPath id='clip1_6025_39726'>
                        <rect
                          width='18'
                          height='18'
                          fill='white'
                          transform='translate(9 8.9375)'
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <Link
                  to={{
                    pathname: '/profile',
                    state: {
                      linkTab: 'Plans',
                      team: cur_user.selected_team,
                    },
                  }}
                >
                  <div className='trial-upgrade-btn'>
                    {isTrialing.data ? (
                      <div className='trial-upgrade-btn-text'>Upgrade</div>
                    ) : (
                      <div className='trial-upgrade-btn-text'>Get Started</div>
                    )}
                  </div>
                </Link>
              </div>
            )}
            {isTrialing.data ? (
              <div className='free-trial-text-group'>
                <div className='free-trial-text-primary'>Free Trial Active</div>
              </div>
            ) : (
              <div className='free-trial-text-group'>
                {!cur_user?.basics?.attributes?.bite_pro_trial_used?.includes(
                  'true',
                ) ? (
                  <div className='free-trial-text-primary'>
                    Start FREE Trial
                  </div>
                ) : (
                  <div className='free-trial-text-primary'>
                    Free Trial Expired
                  </div>
                )}
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default NewSidebar;
